import { Schema } from "effect";
import { Author, DocVersion, JudgeAuthor, StructuredAnchor } from "./audit.ts";
import { BlockLocator, SpanLocator } from "./block-locator.ts";
import { DraftId } from "./collab.ts";
import {
  CefrLevel,
  CurriculumLanguage,
  CurriculumPhase,
  CurriculumRole,
  CurriculumTags,
  PhaseTimestamps,
} from "./curriculum-tags.ts";
import { EdgeKind, NodeId, NodeKind } from "./dep-graph.ts";
import { HeadingDepth, Section } from "./document.ts";
import { FindingSeverity } from "./finding-summary.ts";
import {
  AnnotationId,
  DocumentId,
  LanguagePackDoc,
  LanguagePackPath,
  ModuleTarget,
  SuggestionId,
  ViewerId,
} from "./ids.ts";
import { AnnotationScope, EditOperation } from "./review.ts";
import { canonicalizeRuleId } from "./rule-id.ts";

// Decode-at-boundary Schemas for the MCP semantic ops (R12). Tool args cross the
// untrusted MCP boundary and are decoded once with `Schema.decodeUnknown*` at the
// handler edge; everything inward trusts the decoded type. Rubric edits reuse the
// write/resolve ops against a `resources/*.md` DocumentId — no separate op surface.

// Agent-supplied free text (a rationale, a rule citation, a gate reason). Models occasionally
// double-escape quotes in their tool-call JSON, so a decoded value can arrive carrying a literal
// backslash before a quote (`\"`) — never intended in prose, and it would otherwise render as a
// stray backslash and persist into the document and audit log. Strip it as the text crosses the
// boundary so everything inward stores the clean character. Decode-only: the escape is not
// reconstructable, so encode is identity (these args are never serialized back out).
const AgentText = Schema.transform(Schema.String, Schema.String, {
  strict: true,
  decode: (text) => text.replace(/\\(?=")/g, ""),
  encode: (text) => text,
});

// A `rule` citation crosses the same boundary as `AgentText` but is additionally
// canonicalised to the bare id (`[U6]`/`[unit-plan-rules] U6` → `U6`) so the inline token,
// the audit event, and the idempotency compare all store one notation (R1). `manual:*`
// sentinels and unrecognised free text pass through canonicalisation unchanged.
const RuleCitation = Schema.transform(Schema.String, Schema.String, {
  strict: true,
  decode: (text) => canonicalizeRuleId(text.replace(/\\(?=")/g, "")),
  encode: (text) => text,
});

// Models sometimes emit a structured tool argument — an array, or a tagged-union object — as a JSON
// *string* (`edits: "[{...}]"`, `locator: "{\"_tag\":\"span\",...}"`) instead of the structured value.
// A strict decode then rejects the whole call (`MalformedOutput`), the model re-emits the same large
// payload, and the turn burns a round-trip for zero progress. `JsonTolerant` accepts both shapes at the
// boundary: the structured schema first (a correct value never pays a parse), then `parseJson` as a
// fallback (JSON.parse the string, then decode through the same schema). Same boundary-tolerance spirit
// as `AgentText`. Wrap array/tagged-union args that a model fills from generated content.
const JsonTolerant = <A, I, R>(schema: Schema.Schema<A, I, R>): Schema.Schema<A, I | string, R> =>
  Schema.Union(schema, Schema.parseJson(schema));

// `maxDepth` caps the returned skeleton at depth ≤ N (`1`=curriculum … `4`=module,
// `5`=the module's `##### Authored Exercises` layer — present only on populated modules).
// Omitted returns every heading. The seed flow passes `maxDepth: 2` so a large
// curriculum's outline (hundreds of cycle/module headings) fits the MCP token cap;
// kept sections still carry their full line span (see `sectionsOf`).
// `version` (optional) reads the document as it was at that committed `docVersion`, served by the
// read model; omitted reads current head. A caller reads `docVersion` off a doc-modifying op's own
// echoed result or any doc-modifying event in the audit log, and passes it here to
// slice the exact state that event saw (R5).
// `leadBudget` (optional) caps each section's `lead` (its first-paragraph summary) to N characters;
// omitted uses the default budget. The lead is a digest — full block text is read via `read-block`.
// `cursor` (optional, 0-based) resumes a byte-bounded page of the skeleton from that offset into the
// depth-filtered section list — `maxDepth` shrinks the skeleton but does not guarantee it fits the
// tool-result cap, so the op always byte-bounds its page and returns a `nextCursor` when more remain.
export const ReadOutlineArgs = Schema.Struct({
  documentId: DocumentId,
  maxDepth: Schema.optional(HeadingDepth),
  version: Schema.optional(DocVersion),
  withLeads: Schema.optional(Schema.Boolean),
  leadBudget: Schema.optional(Schema.Int.pipe(Schema.positive())),
  cursor: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
});
export type ReadOutlineArgs = typeof ReadOutlineArgs.Type;

// `read-dep-graph` (CON-449, U4): the dependency-graph view op's decode-at-boundary args. Mirrors
// `ReadOutlineArgs`'s id/version shape, widened to `documentIds` — 1+, **no upper cap** (grill
// decision: the server joins across every provided doc deterministically, Q3) — rather than a
// single id. `version` (optional) mirrors `read-outline`/`read-block`: a historical committed
// version, applied per doc (the same version number reads each provided id's history), omitted
// reads current head. `nodeKinds`/`edgeKinds` reuse the closed `NodeKind`/`EdgeKind` enums so an
// illegal kind is unrepresentable at the boundary, never a stringly-typed filter. `scope` is
// either a `headingPath` prefix (a node's path must start with every segment, in order) or a
// `{ fromUnitPlan, toUnitPlan }` unit-plan span (inclusive) — the two `Scope` shapes
// `queryDepGraph` (U3) already accepts. `focus` narrows to one node id's 1-hop neighborhood.
// `unresolvedOnly` keeps only unresolved edges/refs. Every returned element always carries its
// `headingPath`/`fromHeadingPath` as its actionable address — the same kind of address
// `search-document` returns; the caller `read-block`s it to get an editable `BlockLocator` when it
// needs to act. There is no locator opt-in. `includeMetrics` (default TRUE) is wired with
// `Schema.optionalWith`'s `default` — the same optional-with-default idiom
// `RunChecksToolArgs.view`/`DraftCompleteArgs.resolves` use — so a caller that omits it gets the
// metrics-on (Q1) default without the op needing its own `?? default` fallback. `view` (the
// named-view registry) is deliberately NOT a v1 arg — deferred to U6a if that unit is built.
const UnitPlanRangeArg = Schema.Struct({
  fromUnitPlan: Schema.String,
  toUnitPlan: Schema.String,
});

export const ReadDepGraphArgs = Schema.Struct({
  documentIds: Schema.NonEmptyArray(DocumentId),
  version: Schema.optional(DocVersion),
  nodeKinds: Schema.optional(Schema.Array(NodeKind)),
  edgeKinds: Schema.optional(Schema.Array(EdgeKind)),
  scope: Schema.optional(Schema.Union(Schema.Array(Schema.String), UnitPlanRangeArg)),
  focus: Schema.optional(NodeId),
  unresolvedOnly: Schema.optional(Schema.Boolean),
  includeMetrics: Schema.optionalWith(Schema.Boolean, { default: () => true }),
});
export type ReadDepGraphArgs = typeof ReadDepGraphArgs.Type;

// One `read-outline` section decorated with its `headingPath` — a plain ADDRESS (curriculum → … →
// the block itself), never a hash: a hash is only ever handed out together with the full block text
// it covers (the design rule `BlockDrifted` also follows), and `read-outline` never shows a section's
// full text, only its title/lead/line span. Pass `headingPath` to `read-block` to get the block's
// content plus an editable `BlockLocator` before a `draft-edit`/`draft-replace` — there is no
// shortcut straight from an outline read to an edit.
export const OutlineSection = Schema.Struct({
  ...Section.fields,
  headingPath: Schema.NonEmptyArray(Schema.String),
});
export type OutlineSection = typeof OutlineSection.Type;

// The `read-outline` result: `Outline`'s shape (documentId + paginated sections + nextCursor), with
// each section decorated by its `headingPath` address. A distinct wire type from core's `Outline`
// (which stays the plain internal scanner shape every other caller — `search-document`, `read-block`
// — keeps using unchanged).
export const ReadOutlineResult = Schema.Struct({
  documentId: DocumentId,
  sections: Schema.Array(OutlineSection),
  nextCursor: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
});
export type ReadOutlineResult = typeof ReadOutlineResult.Type;

// `list-curricula` pages the document index so a `curricula/` root holding many documents can never
// overflow the tool-result cap. `cursor` (optional, 0-based) resumes from that offset into the sorted
// id list; the op caps each page and returns a `nextCursor` when more remain.
//
// The optional filters are the caller's lookup dimensions: `documentId` (one document — the metadata
// query), the IDENTITY tags (`level`, `language`, `l1`), and where a document stands (`role`, `phase`,
// `inRework`). Each supplied filter must match a document's current value exactly. An identity filter
// therefore excludes untagged documents by construction, while role and phase always resolve and so
// never exclude on absence. They decode through the same closed schemas the values are stored under —
// a malformed filter ("beginner") fails the decode instead of silently matching nothing. Filters apply
// before pagination; the cursor pages the filtered list. So "the canonical document for the previous
// level" is one call (pair + level + `role: canonical`), and so is "everything awaiting structural
// review" (`phase: structure`).
export const ListCurriculaArgs = Schema.Struct({
  cursor: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  // Narrow to ONE document, which makes this op double as "tell me everything about this curriculum":
  // a caller already working on a document asks for its own row rather than paging the whole root to
  // find it. There is no separate metadata op because the row IS the metadata — one definition, one
  // call, and the agent's answer can never drift from the index's.
  documentId: Schema.optional(DocumentId),
  level: Schema.optional(CefrLevel),
  // `language` is the L2 taught; `l1` the learner's own. Filtering on both is how a caller narrows
  // to one language PAIR — the grouping the index is built around.
  language: Schema.optional(CurriculumLanguage),
  l1: Schema.optional(CurriculumLanguage),
  // The two axes that replaced `status`. `inRework` is separate from `phase` on purpose: a document in
  // rework is still `phase: published`, so "show me what needs attention" cannot be expressed as a
  // phase value.
  role: Schema.optional(CurriculumRole),
  phase: Schema.optional(CurriculumPhase),
  inRework: Schema.optional(Schema.Boolean),
});
export type ListCurriculaArgs = typeof ListCurriculaArgs.Type;

// `read-findings` (ADR-0051): the agent's finding-review surface. A backlog read of the open findings
// for one document, with full contents. Optional filters narrow the set server-side so the payload
// stays bounded on a large backlog: `author` selects the human (manual/`expert`) findings or the
// automated judge findings; `sinceHours` keeps only findings created within the last N hours (server
// clock). Filters apply before pagination; the op then pages the newest-first result the same way as
// `read-pending`/`list-curricula` — `cursor` (optional, 0-based) resumes from that offset, `limit`
// caps the page, and the result returns a `nextCursor` when more remain. Filtering before the
// per-finding head slice keeps a filtered page cheap even when the whole backlog is large.
export const ReadFindingsArgs = Schema.Struct({
  documentId: DocumentId,
  author: Schema.optional(Schema.Literal("human", "judge")),
  sinceHours: Schema.optional(Schema.Number.pipe(Schema.positive())),
  cursor: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  limit: Schema.optional(Schema.Int.pipe(Schema.positive())),
});
export type ReadFindingsArgs = typeof ReadFindingsArgs.Type;

// `author` is the permanent manual-vs-judge identity (CONTEXT.md). A judge pass declares
// its own value; the server-side manual-annotation path supplies `expert` directly. The
// server cannot infer which judge is calling at the MCP boundary, so the caller declares it —
// but only ever as a judge: the op's internal contract carries the full `Author` union (so the
// trusted REST/human edge can pass `expert`), while the agent-facing tool parameters below narrow
// it to `JudgeAuthor`, so an MCP caller cannot forge a manual `expert` finding.
//
// NOTE (block-locator refactor plan U2): this schema — and its `WriteAnnotationToolArgs`/
// `AnnotationDraft`/`WriteAnnotationsArgs` siblings below — currently has no consuming op (the
// carrier `write-annotation`/`write-suggestion` ops were already retired, ADR-0028); the live
// finding-write path is the `annotate` MCP tool (`AnnotateArgs`, below). `locator` still moves from
// the retired `span`/`region`/`boundary`/`anchored` union to `SpanLocator` here so these schemas stay
// consistent with the new locator model if/when they are wired back up or removed.
export const WriteAnnotationArgs = Schema.Struct({
  documentId: DocumentId,
  locator: SpanLocator,
  author: Author,
  rule: RuleCitation,
  rationale: AgentText,
  // The Expert's problem scope (manual path only). Optional and orthogonal to the citation — the
  // op stamps it onto the event when present. Judge/classify callers omit it.
  scope: Schema.optional(AnnotationScope),
});
export type WriteAnnotationArgs = typeof WriteAnnotationArgs.Type;

// The agent-facing parameter schema for the `write-annotation` tool: identical to the op's internal
// contract but with `author` narrowed to `JudgeAuthor`, so a judge/classify agent cannot assert
// `expert`. The MCP framework decodes incoming tool calls against this schema and rejects a spoofed
// `expert` before the handler runs.
export const WriteAnnotationToolArgs = Schema.Struct({
  ...WriteAnnotationArgs.fields,
  author: JudgeAuthor,
});
export type WriteAnnotationToolArgs = typeof WriteAnnotationToolArgs.Type;

// `write-structured-annotation` (ADR-0045): the collab surface's manual-authoring op. Unlike
// `write-annotation`, there is no text `Locator` to resolve against a document that may carry
// review markup — a curriculum's bytes are always plain markdown post-pivot (CONTEXT.md), and the
// CM client already knows the selection's exact head-relative offsets and the `collabVersion` it
// read them at. The caller hands the op a `StructuredAnchor` directly; the op's only job is to
// verify it against the reconstructed text at that version before minting a permanent
// `annotation.created.v2` event (never a `Locator` search, never a markup splice). Like
// `WriteAnnotationArgs`, `author` carries the full `Author` union — this op has NO MCP-tool
// counterpart (mirroring `write-annotation`'s own retirement from the agent surface, ADR-0028), so
// there is no agent-facing narrowed variant: the REST/human edge (`rest.ts`) is the only caller,
// and it always passes `expert`.
export const WriteStructuredAnnotationArgs = Schema.Struct({
  documentId: DocumentId,
  anchor: StructuredAnchor,
  author: Author,
  rule: RuleCitation,
  rationale: AgentText,
  // The person authoring a manual finding (ADR-0051): the REST/human edge passes the requester's
  // `ViewerId` so the finding is attributed to who wrote it. Optional because the `run-checks` judge
  // path also calls this op and a judge finding has a *role* author but no person — it omits it.
  createdBy: Schema.optional(ViewerId),
  // The stamped real display name for the person in `createdBy` (mirrors
  // `ConversationStarted.ownerDisplayName`, ADR-0050 Decision 9) — see `audit.ts` for the shape and
  // rationale. `rest.ts`'s manual path passes it alongside `createdBy`; the judge path passes neither.
  createdByDisplayName: Schema.optional(Schema.String),
  // CON-734 (U3): threaded straight onto `AnnotationCreatedStructured` — see `audit.ts` for the
  // shape and the never-defaulted rationale. `run-checks.ts` passes both; `rest.ts`'s manual path
  // passes neither.
  checkId: Schema.optional(Schema.String),
  partId: Schema.optional(Schema.String),
  // CON-737 (U6): the manual-finding stamped severity — see `audit.ts` for the shape and the
  // never-defaulted rationale. `rest.ts`'s manual path passes it when a severity picker supplies
  // one; the judge path never does (judge severity is derived, not stamped).
  severity: Schema.optional(FindingSeverity),
});
export type WriteStructuredAnnotationArgs = typeof WriteStructuredAnnotationArgs.Type;

// What an annotation write echoes back: the minted id and the stored head-relative anchor (D12). The
// one result shape in this file, and here for the reason ADR-0091 rule 3 gives — it is genuinely
// shared. `@curriculum-studio/mcp` *declares* it (the `annotate` tool's `success` schema) while
// `@curriculum-studio/curriculum-ops` *produces* it (`write-structured-annotation`, which the REST
// edge and the judge's per-failure write both call), so neither package can own it without the other
// importing across.
export const AnnotateResultView = Schema.Struct({
  annotationId: AnnotationId,
  anchor: StructuredAnchor,
});
export type AnnotateResultView = typeof AnnotateResultView.Type;

// One finding in a `write-annotations` batch (ADR-0023): the same per-finding fields as a single
// `write-annotation`, minus the `documentId`/`author` the batch shares across every draft. `rule`
// and `rationale` cross the same forgery/canonicalisation boundary (`RuleCitation`/`AgentText`) the
// single write uses, so a draft decodes identically whether sent alone or in a batch.
export const AnnotationDraft = Schema.Struct({
  locator: SpanLocator,
  rule: RuleCitation,
  rationale: AgentText,
});
export type AnnotationDraft = typeof AnnotationDraft.Type;

// `write-annotations` (batch): one `documentId`/`author` and a non-empty list of drafts, committed
// in array order (ADR-0023). The list must be non-empty — an empty batch is a no-op the caller
// should never send, so it is rejected at the boundary rather than silently succeeding.
export const WriteAnnotationsArgs = Schema.Struct({
  documentId: DocumentId,
  author: Author,
  annotations: JsonTolerant(Schema.NonEmptyArray(AnnotationDraft)),
});
export type WriteAnnotationsArgs = typeof WriteAnnotationsArgs.Type;

// The agent-facing parameter schema for the `write-annotations` batch tool: `author` narrowed to
// `JudgeAuthor` for the same forgery guard as the single write. Only judges ever batch-write.
export const WriteAnnotationsToolArgs = Schema.Struct({
  ...WriteAnnotationsArgs.fields,
  author: JudgeAuthor,
});
export type WriteAnnotationsToolArgs = typeof WriteAnnotationsToolArgs.Type;

// `strip-annotation` is the Expert's retraction of an own manual annotation, valid any time it is
// still open (ADR-0044: a manual annotation is no longer classified, so "open" means simply
// "still pending"). Only the id is needed — the op reads author and lifecycle state from the
// audit log, never trusting the caller for them.
export const StripAnnotationArgs = Schema.Struct({
  documentId: DocumentId,
  annotationId: AnnotationId,
});
export type StripAnnotationArgs = typeof StripAnnotationArgs.Type;

// `amend-suggestion` (new op, Q8): replace a PENDING suggestion's edit operations and reason under
// the SAME `suggestionId` — the check-amend self-correction loop's write. `resolves` is not a
// parameter: the op preserves the existing suggestion's `resolves` (unioned with any newly-folded
// collateral), never accepting a caller-supplied replacement for it. Fails `OpError` if the
// suggestion is not PENDING (already accepted/rejected, or unknown).
export const AmendSuggestionArgs = Schema.Struct({
  documentId: DocumentId,
  suggestionId: SuggestionId,
  reason: AgentText,
  operations: JsonTolerant(Schema.NonEmptyArray(EditOperation)),
});
export type AmendSuggestionArgs = typeof AmendSuggestionArgs.Type;

// `resolve` is the unified gate write-back; the decision is a tagged union over the
// possible gate/triage outcomes.
//
// The Ignore affordance (ADR-0049 D1): the single user action on a finding — "this is not a real
// issue / do not flag again". Replaces the retired agree/disagree/close decision family (removed from
// the union in U7). Manual and judge findings share it. `reason` is the free-text why (may be empty —
// ignoring is low-ceremony). The retired `annotation.agreed`/`.disagreed`/`.closed` audit *events*
// stay decodable + folded (U2) so old logs replay; only the client-facing DECISION is gone.
export const IgnoreAnnotation = Schema.TaggedStruct("ignore-annotation", {
  annotationId: AnnotationId,
  reason: AgentText,
});
export const CommentOnlyDecision = Schema.TaggedStruct("comment-only", {
  annotationId: AnnotationId,
  reason: AgentText,
});
export const WithdrawDecision = Schema.TaggedStruct("withdraw", {
  annotationId: AnnotationId,
  suggestionId: SuggestionId,
  reason: AgentText,
});
export const ResolveDecision = Schema.Union(
  IgnoreAnnotation,
  CommentOnlyDecision,
  WithdrawDecision,
);
export type ResolveDecision = typeof ResolveDecision.Type;

export const ResolveArgs = Schema.Struct({
  documentId: DocumentId,
  decision: JsonTolerant(ResolveDecision),
});
export type ResolveArgs = typeof ResolveArgs.Type;

// The REST gate's success/response wire shapes (R5). Defined once here so the server (`rest.ts`,
// which encodes them on its `HttpApi` endpoints) and the web client (`gate-actions.ts`, which
// decodes them) share one definition rather than two byte-identical copies that could drift.
export const ResolveSuccess = Schema.Struct({
  status: Schema.Literal("ok"),
  clearedAnnotationIds: Schema.Array(AnnotationId),
});
export type ResolveSuccess = typeof ResolveSuccess.Type;

// One entry of the `/api/curricula` (and `/api/resources`) index, and — because `list-curricula`
// takes a `documentId` filter — the whole of what either a person or an agent can know ABOUT a
// curriculum without opening it: the rooted id, its H1 title, its IDENTITY tags (level + the language
// pair, from the latest tags-bearing event, genesis or `document.tags-updated`), where it stands
// (role, phase, the published pin, any open rework, what it superseded), and its dates. `tags` is
// absent for resources and for documents created before tags existed (seeded or legacy), which both
// surfaces render as untagged; role and phase always resolve. ONE definition, so the agent's view of
// a document and the Expert's can never drift apart.
export const CurriculumListItem = Schema.Struct({
  documentId: DocumentId,
  title: Schema.optional(Schema.String),
  tags: Schema.optional(CurriculumTags),
  // Role and phase always resolve — an untagged legacy curriculum reads as the fold's defaults rather
  // than as absent, so a consumer never has to invent a fallback.
  role: CurriculumRole,
  phase: CurriculumPhase,
  // The version learners see. Absent until first published; it holds while `docVersion` moves on, so a
  // consumer compares the two to show unreleased drift.
  publishedVersion: Schema.optional(DocVersion),
  // Set while part of a published document is back at an earlier phase.
  reworkPhase: Schema.optional(CurriculumPhase),
  // "pending" while a `generating`-origin genesis (KTD1) has no terminal event yet, "failed" once
  // its run's `document.generation-failed` lands, absent otherwise (an ordinary document, or one
  // whose run resolved with `document.generation-completed` — KTD3).
  generationStatus: Schema.optional(Schema.Literal("pending", "failed")),
  // The document this one replaced when promoted (succession, not clone lineage).
  supersedes: Schema.optional(DocumentId),
  // Chain facts, projected from the document's own log — no new bookkeeping.
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
  docVersion: DocVersion,
  // The production milestones: when each phase was ENTERED (so "structure passed" is when `exercises`
  // began) and when the document FIRST went live. Only phases actually reached appear; later releases
  // move the pin, not `publishedAt`.
  phaseEnteredAt: Schema.optional(PhaseTimestamps),
  publishedAt: Schema.optional(Schema.DateTimeUtc),
});
export type CurriculumListItem = typeof CurriculumListItem.Type;

// `search-document` (U3): turn a text query into ready-made, document-unique anchors with section
// context — collapsing discovery and anchor guessing into one tool call (R2). `query` is a
// NonEmpty string so an empty query fails the decode rather than silently returning zero results
// (which would masquerade as not-found — `occurrences` returns 0 for an empty needle). Multi-line
// queries (containing a newline character) are rejected explicitly: normalised text is single-line
// per occurrence and a newline in the needle would never match. `maxResults` caps the returned
// matches list (default 10); `totalCount` on the result always reflects the true match count.
export const SearchDocumentToolArgs = Schema.Struct({
  documentId: DocumentId,
  query: Schema.NonEmptyString,
  maxResults: Schema.optional(Schema.Int.pipe(Schema.positive())),
});
export type SearchDocumentToolArgs = typeof SearchDocumentToolArgs.Type;

// One match in a `search-document` result. `line` is the 1-based line in the document where the
// match starts. `context` is the containing line (or a trimmed window) showing the match in situ.
// `headingPath` is the full ancestor heading path of the containing block (same shape as `run-checks`
// target) — a plain ADDRESS, empty only for a match in the document preamble (before the first
// heading, which has no enclosing block). This carries no hash and no `SpanLocator` (block-locator
// refactor plan U9): a hash is only ever handed out together with the full block text it covers, and
// a search match sees only the query's own line, never the whole block — so a hit is a lead to
// `read-block`, not an editable token. `read-block` the `headingPath` to get the block's content plus
// a `BlockLocator`/`SpanLocator` you can pass into `annotate`/`draft-edit`/`draft-replace`.
export const SearchDocumentMatch = Schema.Struct({
  line: Schema.Int.pipe(Schema.positive()),
  context: Schema.String,
  headingPath: Schema.Array(Schema.String),
});
export type SearchDocumentMatch = typeof SearchDocumentMatch.Type;

// The `search-document` success result. `matches` is the capped list; `totalCount` is the real
// count (so the agent can see when the list was truncated).
export const SearchDocumentResult = Schema.Struct({
  matches: Schema.Array(SearchDocumentMatch),
  totalCount: Schema.Int.pipe(Schema.nonNegative()),
});
export type SearchDocumentResult = typeof SearchDocumentResult.Type;

// ─── Agent draft + annotate op args (ADR-0044, stateless single-server surface) ───
//
// The MCP surface is STATELESS: there is no per-session server state, so — like the shared read ops —
// every draft/annotate op carries its own `documentId`, and every op that targets an existing draft
// carries the `draftId` returned by `draft-open`. The coordinate basis for `draft-edit` travels on the
// wire (`baseVersion`/`draftVersion`) rather than living in a server `Ref`: an edit echoes the version
// the offsets were read against, and the server refuses (stale) if the live draft has moved on. This
// makes the surface re-entrant and usable by any MCP client — the harness agent AND a directly
// attached claude-code — with no bespoke session.

// `draft-open`: fork a fresh whole-document draft from the current head. `reason` is optional
// context for the reviewer — the WHY (the problem being fixed, the request being answered), not a
// restatement of what the draft will change. The server mints the id and stamps the draft
// AGENT-owned, so it always routes through the human accept/reject gate (gate 2) — an MCP-opened
// draft never self-lands.
export const DraftOpenArgs = Schema.Struct({
  documentId: DocumentId,
  reason: Schema.optional(Schema.String),
});
export type DraftOpenArgs = typeof DraftOpenArgs.Type;

// `expand-exercises` (ADR-0084): trigger the studio's server-side exercise expander against a document,
// authoring exercises into an AGENT-owned draft that lands at gate 2. The server reads the head text
// itself and owns the model/effort (never the caller). Returns immediately with the draft id; the run
// streams into the draft in the background, so the agent follows it with `draft-diff`/`read-block`
// rather than blocking (the 10-minute MCP op ceiling).
//
// `modules` is the SAME scope the human surface uses, and REQUIRED (U0): naming `unit.cycle.module`
// targets authors only those modules, so an agent asked to fill in one module is not forced to
// re-author the whole curriculum. Every run is named-module-scoped by construction — there is no
// whole-document run to fall back to. A named module that is not in the document refuses the call
// outright.
export const ExpandExercisesArgs = Schema.Struct({
  documentId: DocumentId,
  modules: Schema.NonEmptyArray(ModuleTarget),
});
export type ExpandExercisesArgs = typeof ExpandExercisesArgs.Type;

// `draft-edit` (block-locator refactor plan U4): replace ONE WHOLE block, addressed by its
// `BlockLocator` (headingPath + content hash), with `newContents`. The locator is resolved
// SERVER-SIDE against the draft's own current applied text (the draft buffer) via `resolveBlockLocator`
// — a stale `contentHash` fails `BlockDrifted` (carrying the block's fresh locator) rather than
// silently re-resolving, so drift is always an explicit re-read+retry, never a guessed merge (KTD1/
// KTD6). `newContents` is always ONE top-level string carrying the block's heading line through its
// body (so a heading casing/relift is expressible) — never a nested array — which is what makes a
// large block payload reliably decodable (KTD3): the old `edits: NonEmptyArray({locator, insert})`
// shape forced the model to hand-escape a whole block inside a nested structure, and large payloads'
// escaping broke in practice. Empty `newContents` deletes the block (delete is the empty replace, no
// separate op). Single block per call; no batch — multiple tool calls cover multi-block work.
export const DraftEditArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
  locator: BlockLocator,
  newContents: Schema.String,
});
export type DraftEditArgs = typeof DraftEditArgs.Type;

// `draft-replace` (block-locator refactor plan U5): replace the UNIQUE `text` inside a `SpanLocator`'s
// `block` with `replace` — the surgical sibling of `draft-edit`'s whole-block replace, for a small
// change that does not warrant re-emitting the whole block. The locator is resolved SERVER-SIDE
// against the draft's own current applied text via `resolveSpanLocator`: the same `BlockDrifted` hash
// gate as `draft-edit` covers `locator.block`, and `locator.text` must then be unique WITHIN that
// block's own span (R-E) — absent fails `SpanNotFound`, more than one occurrence fails `SpanAmbiguous`
// (widen the text). There is no replace-all: uniqueness is required, so a multi-occurrence `text` never
// silently rewrites more than the one span the agent quoted, and a `text` also occurring OUTSIDE the
// block is never touched (the search never leaves the block's extent). Single span per call; no batch.
export const DraftReplaceArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
  locator: SpanLocator,
  replace: Schema.String,
});
export type DraftReplaceArgs = typeof DraftReplaceArgs.Type;

// `draft-insert` (block-locator refactor plan U8/KTD10): add a NEW block at a position relative to
// an existing `anchor` block — the general "add" primitive (draft-edit only replaces an existing
// block, so it has no way to add one). `position` is `before`/`after` (a sibling of the anchor, at
// the anchor's own depth) or `first-child` (nested one depth under the anchor, before its first
// existing child). There is no dedicated move op: a move is `draft-insert` at the destination
// followed by `draft-edit(source, "")` to delete the original — insert runs first so a mid-sequence
// failure leaves a recoverable duplicate, never a lost block (R-I). `newContents` is ONE top-level
// string carrying the new block's own heading line through its body (KTD3) — never a nested array.
// The server hash-gates `anchor` exactly like `draft-edit`/`draft-replace` (`BlockDrifted` on a stale
// hash, `SectionNotFound` on an unknown heading path); it then rewrites `newContents`'s own heading
// depths to fit the destination nesting (`rewriteDepths`), so the agent supplies naturally-leveled
// content and the server corrects it, never the other way around.
export const DraftInsertArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
  anchor: BlockLocator,
  position: Schema.Literal("before", "after", "first-child"),
  newContents: Schema.String,
});
export type DraftInsertArgs = typeof DraftInsertArgs.Type;

// `draft-diff` / `draft-complete`: address one existing draft by id on its document.
export const DraftDiffArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
});
export type DraftDiffArgs = typeof DraftDiffArgs.Type;

// `draft-withdraw` (ADR-0044 extension, thread 3): discard the agent's OWN draft — the agent's escape
// valve for a draft it no longer wants to pursue (the stale-draft resolver's REBASE path withdraws the
// stale draft before opening a fresh one against head; its WITHDRAW path withdraws and stops). The
// server refuses to withdraw a HUMAN-owned draft (`DiscardRequiresHuman`, folded to `OpError`) — a
// human's draft is discarded only by a human over the collab surface. `reason` is optional
// audit-context stamped on the recorded `draft.discarded` event.
export const DraftWithdrawArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
  reason: Schema.optional(Schema.String),
});
export type DraftWithdrawArgs = typeof DraftWithdrawArgs.Type;

// `description` is the mandatory one-line explanation of WHY the draft exists — the problem it
// fixes or the request it answers — not a restatement of the diff. The server refuses a submit
// whose description is empty/whitespace (`DraftDescriptionRequired`, folded to `OpError`), so the
// gate-2 reviewer and the audit log always have the author's rationale.
// `resolves` names the finding(s) this draft answers (ADR-0049 D3): the agent's chat-turn skill
// frames each draft as resolving specific annotations. Optional (default []) — a draft may answer no
// modeled finding (a routine copy-edit). Its land emits one `annotation.resolved.v2` per open id.
export const DraftCompleteArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: DraftId,
  description: AgentText,
  resolves: Schema.optionalWith(JsonTolerant(Schema.Array(AnnotationId)), { default: () => [] }),
});
export type DraftCompleteArgs = typeof DraftCompleteArgs.Type;

// `annotate`: a `SpanLocator` (block-locator refactor plan U2 — the same span shape `draft-replace`
// uses, KTD4/KTD5), resolved server-side against HEAD text via `resolveSpanLocator`. `rule`/
// `rationale` cross the same forgery/canonicalisation boundary the other write ops use. Text existing
// only in an unlanded draft refuses with `SpanNotFound` (land first, annotate after); a block whose
// heading path is unknown refuses `SectionNotFound`; a block that has since drifted refuses
// `BlockDrifted` (re-read-block and retry). `author` is narrowed to `JudgeAuthor`: a within-unit
// check stamps `unit-judge`, a cross-unit check `cross-unit-judge`, and an MCP caller can never forge
// a manual `expert` finding — the illegal value is unrepresentable at the boundary.
export const AnnotateArgs = Schema.Struct({
  documentId: DocumentId,
  locator: SpanLocator,
  rule: RuleCitation,
  rationale: AgentText,
  author: JudgeAuthor,
});
export type AnnotateArgs = typeof AnnotateArgs.Type;

// `read-block` (block-locator refactor plan U3): return one WHOLE block's markdown plus its
// `BlockLocator` — the natural companion read for `draft-edit`'s whole-block replace (U4) and
// `draft-replace`'s within-block `SpanLocator` (U5). Not paginated: a block-hash compare-and-swap
// needs the locator to describe the SAME bytes the agent is shown, so
// the whole block comes back in one call (a `draft-edit` replace payload is symmetrically whole-block,
// KTD3). `headingPath` addresses the block by its heading ancestry (curriculum → … → the block
// itself), the same shape `read-outline`/`search-document` return. With `draftId` present the block is
// read from that draft's own applied text (the draft buffer) rather than committed head, so the
// returned locator's hash matches the buffer the agent is about to edit (KTD1) — omit it to read
// current head.
export const ReadBlockArgs = Schema.Struct({
  documentId: DocumentId,
  draftId: Schema.optional(DraftId),
  headingPath: JsonTolerant(Schema.NonEmptyArray(Schema.String)),
});
export type ReadBlockArgs = typeof ReadBlockArgs.Type;

// `text` is the block's verbatim markdown (heading line through its last line, subtree included —
// KTD3a); `locator` is its `BlockLocator`, echoed back verbatim into `draft-edit`/`draft-replace`.
export const ReadBlockResult = Schema.Struct({
  text: Schema.String,
  locator: BlockLocator,
});
export type ReadBlockResult = typeof ReadBlockResult.Type;

// `read-language-pack` (ADR-0097): the agent's READ-ONLY consult surface over the six
// language-pack document types. Omit `path` to list every doc (optionally narrowed to one
// language with `languageCode`); pass `path` to read one doc's full content. There is no
// write op for this document class — the agent consults these when editing curricula, it
// never edits them.
export const ReadLanguagePackArgs = Schema.Struct({
  path: Schema.optional(LanguagePackPath),
  languageCode: Schema.optional(Schema.String),
});
export type ReadLanguagePackArgs = typeof ReadLanguagePackArgs.Type;

// With `path`: `doc` carries the single requested document, `docs` is empty. Without `path`:
// `docs` carries the (optionally language-scoped) listing, `doc` is absent. Two disjoint result
// shapes in one Struct (rather than a union) keeps the tool's `success` Schema simple for the
// agent to consume — it always inspects `path`-vs-not on its own call to know which field to read.
export const ReadLanguagePackResult = Schema.Struct({
  doc: Schema.optional(LanguagePackDoc),
  docs: Schema.Array(LanguagePackDoc),
});
export type ReadLanguagePackResult = typeof ReadLanguagePackResult.Type;
